/**
 * The fixed values the Domains & Projects navigator is built around.
 *
 * They live together rather than in four files because they are one theme — the navigator's own
 * tuning — and splitting a theme across files is how two of them drift apart while still being read
 * side by side. Each one is a decision, so each carries the reason it has the value it has.
 */
export const PROJECT_NAVIGATOR = {
	/**
	 * The single-page ceiling on how many projects the navigator asks for — NOT a page size it pages
	 * through. The tree renders every project it holds at once, grouped under its domain, because the
	 * user searches and scans it rather than stepping through it; there is no "next page" affordance
	 * anywhere in the design, so a second page would simply be invisible. One request at this size
	 * therefore has to cover the whole account, and 1000 is the headroom over the largest real one.
	 *
	 * The store treats a total above this as a defect rather than a paging cue: when the server reports
	 * more rows than were returned, it warns, and the tree shows the first 1000. A silent truncation
	 * here reads to the user as "that project does not exist", which is the failure this ceiling exists
	 * to make noisy instead.
	 */
	PAGE_SIZE: 1000,
	/**
	 * What sits between a project's number and its name in the row label — `12 - Riverside Tower`.
	 *
	 * Part of the seam rather than decoration: the label format is pinned by the domain model, and the
	 * markup half's search filter matches against this exact rendered string, so the number is findable
	 * only because the separator keeps it in the label. It lives here so the two halves cannot drift
	 * into two spellings of the same format.
	 */
	PROJECT_LABEL_SEPARATOR: ' - ',
	/**
	 * The label of the one group each domain holds its projects under — every domain has exactly this
	 * group, and today no other. It is a literal rather than a translation key because the headings
	 * around it are organization names coming from the server, and this row has to read as one of the
	 * same kind rather than as the only translated string among them.
	 */
	PROJECTS_GROUP_LABEL: 'PROJECTS',
	/**
	 * Prefix of the storage key remembering the last project opened. The user id is appended to it, so
	 * two accounts on one browser resume at their own last project instead of each other's. Prefixed
	 * with the product name because the origin is shared with everything else the app persists.
	 */
	STORAGE_KEY_PREFIX: 'Efficiently-LastProject',
} as const;
